import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LANGUAGE,
  localizePath,
  normalizeLanguage,
  splitLanguageFromPath,
} from '../../lib/localeRoutes';

const REDIRECT_FLAG = 'dz:langRedirected';

/** Paths that are intentionally single-language and must never be redirected. */
const EXEMPT_PATHS = ['/panel', '/bio'];

function readLanguageCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)i18nextLng=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Keeps i18n in lockstep with the URL, which is the canonical carrier of
 * language. Mounted once, inside the router.
 */
function LanguageRouter() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const redirectChecked = useRef(false);

  const { lang: pathLanguage, path: barePath } = splitLanguageFromPath(pathname);

  // 1. The URL decides which language renders.
  useEffect(() => {
    if (i18n.language !== pathLanguage) {
      void i18n.changeLanguage(pathLanguage);
    }
  }, [i18n, pathLanguage]);

  // 2. A returning visitor who previously chose a language gets sent to that
  //    language's URL — once per session, and only from an un-prefixed path.
  //    Crawlers carry no cookie, so they are never redirected and always index
  //    the English URL they requested.
  useEffect(() => {
    if (redirectChecked.current) return;
    redirectChecked.current = true;

    if (pathLanguage !== DEFAULT_LANGUAGE) return;
    if (EXEMPT_PATHS.includes(barePath)) return;

    let alreadyRedirected = false;
    try {
      alreadyRedirected = sessionStorage.getItem(REDIRECT_FLAG) === '1';
    } catch {
      // Private mode or blocked storage: skip the redirect rather than loop.
      return;
    }
    if (alreadyRedirected) return;

    const preferred = normalizeLanguage(readLanguageCookie());
    if (preferred === DEFAULT_LANGUAGE) return;

    try {
      sessionStorage.setItem(REDIRECT_FLAG, '1');
    } catch {
      return;
    }
    navigate(localizePath(barePath, preferred), { replace: true });
  }, [barePath, navigate, pathLanguage]);

  return null;
}

export default LanguageRouter;
